<template>
  <div :class="disabled && 'cursor-not-allowed'">
    <label
      v-if="title && !hideTitle"
      class="text-gray-700 font-inter text-sm font-medium mb-1.5 flex"
    >
      {{ $t(`placeholder.${title}`) }}
      <span v-if="rules.includes('required')" class="text-text-color-danger pl-1">*</span>
    </label>
    <div class="relative">
      <div
        :class="[isErrorAvailable && 'bg-bg-error border-text-color-danger', customClasses]"
        class="flex rounded-lg flex-row pl-3.5 gap-2 border"
      >
        <div class="flex items-center gap-1.5 cursor-pointer" @click="toggleCountrySelectDropdown">
          <span class="text-base font-normal">{{ selectedCountry }}</span>
          <Icon
            class="icon"
            icon="chevronBottom"
            color="primary-grey-light"
            height="5"
            width="10"
          />
        </div>
        <div class="flex items-center w-full text-base font-normal">
          <span class="mr-1">{{ selectedCountryCode }}</span>
          <Field
            v-model="activePhoneNumber"
            :class="isErrorAvailable && 'error-bg bg-bg-error'"
            class="focus:outline-none w-full"
            :placeholder="placeHolder"
            :name="name"
            type="text"
            autocomplete="off"
            :maxlength="15"
            :oninput="fieldValueFormat"
            :disabled="disabled"
            :rules="parsedRules"
            @input="updatePhoneNumber"
            @keydown.enter.prevent
          />
        </div>
      </div>
      <OnClickOutside :do="closeCountrySelectDropdown">
        <div
          v-show="countrySelectDropdown"
          class="absolute shadow-xl px-2 z-10 bg-white border rounded-lg mt-2 max-h-80 overflow-auto py-2.5 w-full"
        >
          <div class="flex border items-center gap-2 px-3.5 h-11 rounded-lg">
            <Icon class="icon" icon="search" color="stroke-gray-500" height="15" width="15" />
            <input
              v-model="searchedValue"
              :disabled="disabled"
              class="focus:outline-none w-full text-base"
              placeholder="Search ..."
              @keydown.enter.prevent
            />
          </div>
          <Divider />
          <div class="px-3.5">
            <div
              v-for="(country, index) in filteredCountries"
              :key="index"
              class="flex flex-row w-full py-2.5 hover:bg-gray-50 cursor-pointer text-base"
              @click="updateSelectedValue(country)"
            >
              <span>
                {{ country[0] }}
                <span class="text-gray-500">+{{ country[2] }}</span>
              </span>
            </div>
            <NoRecordFound v-if="!filteredCountries.length" />
          </div>
        </div>
      </OnClickOutside>
    </div>
    <div ref="errorContainer" class="flex justify-end h-5">
      <ErrorMessage v-slot="{ message }" :name="name">
        <small class="error-msg text-text-color-danger text-sm italic">
          <p>{{ displayError(message) }}</p>
        </small>
      </ErrorMessage>
    </div>
  </div>
</template>

<script>
import { Field, ErrorMessage, configure } from 'vee-validate'
import Icon from '@components/icons/icon.vue'
import OnClickOutside from '@components/UiElements/UiOnClickOutside.vue'
import countries from '@src/constants/countriesArray'
import { mapActions, mapState } from 'vuex'
import validationMixin from '@src/mixins/components/validation-mixin.js'
import parsePhoneNumber from 'libphonenumber-js'
import phoneNumberExamples from 'libphonenumber-js/mobile/examples'
import NoRecordFound from '@src/components/BaseComponent/NoRecordFound.vue'
import { validationConfiguration } from '@src/vee-validate/index.js'
import Divider from '@src/components/Divider.vue'

configure(validationConfiguration)

export default {
  name: 'UiPhoneNumber',
  components: {
    Field,
    ErrorMessage,
    Icon,
    NoRecordFound,
    OnClickOutside,
    Divider,
  },
  mixins: [validationMixin],
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: [String, Number, Boolean, Object],
      default: '',
    },
    rules: { type: String, default: '' },
    name: { type: String, default: 'Phone Number' },
    title: { type: String, default: '' },
    customClasses: { type: String, default: 'h-11' },
    isVerified: {
      type: [Boolean, String],
      default: '',
    },
    filter: {
      type: Object,
      default: () => {},
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    countrySelected: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue', 'update', 'emitQuery'],
  data() {
    return {
      countries: countries,
      searchedValue: '',
      filteredCountries: [],
      currentDefaultCountry: 0,
      currentDefaultCountryCode: 2,
      selectedCountry: '',
      isErrorAvailable: false,
      selectedCountryCode: '',
      activePhoneNumber: '',
      placeHolder: '(101)',
      countrySelectDropdown: false,
      allCountries: null,
      countryNameIndex: 1,
    }
  },
  computed: {
    ...mapState({
      defaultCountry: (state) => state.layout?.generalSettings?.default_country,
      permittedCountries: (state) => state.settings.allowedCountries,
    }),
    parsedRules() {
      if (!this.rules) return
      let rulesList = this.rules.split('|')
      rulesList = rulesList.map((val) => {
        if (val === 'validNumber') {
          val = `validNumber:${this.selectedCountry.toUpperCase()}`
        }
        return val
      })
      return rulesList.join('|')
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler() {
        this.errorHandler()
      },
    },
    rules: {
      handler() {
        this.errorHandler()
      },
    },
    countrySelected: {
      handler(val) {
        this.selectedCountry = val
        let country = null

        if (this.permittedCountries?.length) {
          const permittedCountryIds = this.permittedCountries.map((country) => country?.id)
          const filteredPermitted = this.countries.filter((country) =>
            permittedCountryIds.includes(country[this.countryNameIndex]),
          )
          this.allCountries = filteredPermitted
          country = filteredPermitted.find((country) => country[1] === val)
        } else {
          country = this.countries.find((country) => country[1] === val)
        }

        if (country) {
          this.updateSelectedValue(country)
        }
      },
    },
    selectedCountry: {
      handler(val) {
        if (val) this.placeHolder = phoneNumberExamples[val.toUpperCase()]
      },
      immediate: true,
    },
    /**
     * @description SEARCH IS IPLEMENTED HERE
     */
    searchedValue: {
      handler: function (searchString) {
        var filteredCountries = []
        if (this.permittedCountries?.length) {
          const permittedCountryIds = this.permittedCountries?.map((country) => country?.id)
          const filteredPermittedCountries = this.countries.filter((country) =>
            permittedCountryIds?.includes(country[this.countryNameIndex]),
          )
          this.allCountries = filteredPermittedCountries
        } else this.allCountries = this.countries

        searchString = searchString.toLowerCase()
        var withoutPlusSign = searchString?.replace('+', '')
        this.allCountries?.forEach((val, ind) => {
          if (
            val[0].toLowerCase().includes(searchString) ||
            val[1].toLowerCase().includes(searchString) ||
            val[2].toLowerCase().includes(withoutPlusSign)
          ) {
            filteredCountries.push(val)
          }
        })

        this.filteredCountries = filteredCountries
        if (searchString === '' || searchString === ' ') this.filteredCountries = this.allCountries
      },
      immediate: true,
    },
  },
  /**
   * Before mount logic for Edit Number
   *  */
  created() {
    /* SETTING DEFAULT COUNTRIES CODE & HANDLING EDIT*/
    this.selectedCountry = this.defaultCountry?.toUpperCase() || ''
    const defaultCountryCode = this.getDefaultCountryCode()
    this.selectedCountryCode = defaultCountryCode
    if (this.modelValue) {
      this.assignValues(this.modelValue)
    }
  },

  methods: {
    fieldValueFormat(event) {
      let inputValue = event.target.value
      inputValue = inputValue.replace(/\D/g, '')
      this.activePhoneNumber = inputValue
      event.target.value = inputValue
    },
    getDefaultCountryCode() {
      let currentCountry = this.allCountries?.filter(
        (country) => country[1] === this.defaultCountry,
      )
      if (currentCountry && currentCountry.length > 0) {
        return `+${currentCountry[0][this.currentDefaultCountryCode]}`
      }
      return ''
    },
    assignValues(val) {
      /* FOR HALF NUMBER EG. +92301 */
      this.activePhoneNumber = this.addPlusPrefixToPhone(val)
      const parsedObj = parsePhoneNumber(this.activePhoneNumber)
      if (parsedObj && parsedObj.country) {
        /* FOR FULL NUMBER EG. +923014408635 */
        this.activePhoneNumber = parsedObj.nationalNumber
        this.selectedCountry = parsedObj?.country
        this.selectedCountryCode = `+${parsedObj?.countryCallingCode}`
        this.$emit('update:modelValue', parsedObj?.number)
      }
    },
    addPlusPrefixToPhone(number) {
      // Trim any leading or trailing whitespace
      number = number.trim()

      // Check if the number starts with a '+'
      if (!number.startsWith('+')) {
        number = '+' + number
      }

      return number
    },
    updatePhoneNumber($event) {
      /**
       * @HANDLED_EDGE_CASES
       * 1. ADDING + AT START
       * 2. ADDING + ONLY 1 TIME AT START
       * 3. ADDED VALUE IS A NUMBER ONLY
       * 4. ADDED VALUE IS A ZERO
       * 5. CHECK IF INPUT DATA IS NUMBER ONLY
       */
      let isValidData = false
      let query
      if (
        $event?.data === '+' &&
        $event.target.selectionStart === 1 &&
        ($event.target.value.length === 1 || !$event.target.value.substring(1).includes('+'))
      ) {
        isValidData = true
      }
      /**
       * NULL is used for backspace or removing number
       **/
      if (+$event?.data || +$event?.data === 0 || $event?.data === null) {
        isValidData = true
      }
      /* HANDLING SPACE OR DATA SHOULD NOT BE EQUAL TO HERE*/
      if ($event?.data === ' ') {
        isValidData = false
      }
      if (isValidData) {
        const parsedObj = parsePhoneNumber($event?.target.value, this.selectedCountry)
        let returnVal = $event.target.value
        if (parsedObj) {
          returnVal = parsedObj.number
        }
        this.activePhoneNumber = $event.target.value
        this.$emit('update:modelValue', returnVal)
        this.$emit('update', { formattedNumber: returnVal })
        if (this.filter && this.filter.option)
          query = buildWhereQuery(this.filter.option, this.filter.key, returnVal)
        this.$emit('emitQuery', query)
      }
    },
    /**
     * @description For toggling country list model
     */
    toggleCountrySelectDropdown() {
      this.countrySelectDropdown = !this.countrySelectDropdown
    },
    /**
     * @description For toggling country list model
     */
    closeCountrySelectDropdown() {
      this.countrySelectDropdown = false
      this.searchedValue = ''
    },
    /**
     * @description used to select new country.
     */
    updateSelectedValue(selectedCountryObject) {
      const parsedObj = parsePhoneNumber(
        this.activePhoneNumber,
        selectedCountryObject[1].toUpperCase(),
      )
      if (parsedObj) {
        if (this.selectedCountry !== selectedCountryObject[1].toUpperCase()) {
          parsedObj.number = ''
          this.activePhoneNumber = ''
        }
        this.$emit('update:modelValue', parsedObj.number)
        this.$emit('update', { formattedNumber: parsedObj.number })
      }
      this.selectedCountry = selectedCountryObject[1].toUpperCase()
      this.selectedCountryCode = selectedCountryObject[2]
      /* HANDLING COUNTRY CHANGE UPDATE */
      if (this.countrySelectDropdown) this.toggleCountrySelectDropdown()
    },
  },
}
</script>
